"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LoggerProvider = exports.LogLevelValue = exports.Logger = void 0;
/* eslint-disable @typescript-eslint/no-explicit-any */
const browser_logs_1 = require("@datadog/browser-logs");
/**
 * A simple wrapper to facilitate console and datadog logging.
 * Returns a union of standard Console API and the DataDog Brower API.
 * Effectively these are the same, except DatDog does not support the `trace` operator.
 *
 * Recommend using tslog wrapping console output for node applications.
 */
class Logger {
    constructor(module, context) {
        this.dataDogTransport = undefined;
        // Used when the requested level is higher than the generated message.
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        this.noOp = () => { };
        this.scope = module;
        this.context = context;
        this.logLevel = 'info'; // default
    }
    get level() {
        return this.logLevel;
    }
    set level(level) {
        this.logLevel = level;
        if (this.dataDogTransport) {
            this.dataDogTransport.setLevel(level);
        }
    }
    get warn() {
        if (exports.LogLevelValue[this.level] > exports.LogLevelValue['warn']) {
            return this.noOp;
        }
        return this.dataDogTransport
            ? this.dataDogTransport.warn.bind(this.dataDogTransport)
            : console.warn.bind(console, this.format());
    }
    get debug() {
        if (exports.LogLevelValue[this.level] > exports.LogLevelValue['debug']) {
            return this.noOp;
        }
        return this.dataDogTransport
            ? this.dataDogTransport.debug.bind(this.dataDogTransport)
            : console.debug.bind(console, this.format());
    }
    get error() {
        if (exports.LogLevelValue[this.level] > exports.LogLevelValue['error']) {
            return this.noOp;
        }
        return this.dataDogTransport
            ? this.dataDogTransport.error.bind(this.dataDogTransport)
            : console.error.bind(console, this.format());
    }
    get info() {
        if (exports.LogLevelValue[this.level] > exports.LogLevelValue['info']) {
            return this.noOp;
        }
        return this.dataDogTransport
            ? this.dataDogTransport.info.bind(this.dataDogTransport)
            : console.info.bind(console, this.format());
    }
    // Default log bound to 'info'
    get log() {
        if (exports.LogLevelValue[this.level] > exports.LogLevelValue['info']) {
            return this.noOp;
        }
        return this.dataDogTransport
            ? this.dataDogTransport.info.bind(this.dataDogTransport)
            : console.info.bind(console, this.format());
    }
    // trace bound to debug for datadog (lowest level)
    get trace() {
        if (exports.LogLevelValue[this.level] > exports.LogLevelValue['trace']) {
            return this.noOp;
        }
        return this.dataDogTransport
            ? this.dataDogTransport.debug.bind(this.dataDogTransport)
            : console.trace.bind(console, this.format());
    }
    setDataDogTransport(dd) {
        this.dataDogTransport = dd;
    }
    dataDogAvailable() {
        return this.dataDogTransport !== undefined;
    }
    format() {
        const printedContext = this.context
            ? Object.keys(this.context).reduce((accum, curr) => {
                accum += `[${curr}: ${this.context[curr]}] `;
                return accum;
            }, ' ')
            : '';
        return `[${this.scope}]${printedContext}`;
    }
}
exports.Logger = Logger;
exports.LogLevelValue = new Map([
    ['trace', 0],
    ['debug', 100],
    ['warn', 200],
    ['info', 300],
    ['error', 400]
]);
// LoggerProvider acts a simple factory to generate scoped loggers with a name and context.
class LoggerProvider {
    static initializeDatadog(clientToken) {
        if (LoggerProvider.dataDogClientToken != '' && LoggerProvider.dataDogClientToken != clientToken) {
            throw 'Logger must be configured with one client token.';
        }
        try {
            browser_logs_1.datadogLogs.init({
                clientToken: clientToken,
                datacenter: 'us',
                forwardErrorsToLogs: true,
                sampleRate: 100,
                silentMultipleInit: true
            });
        }
        finally {
            LoggerProvider.dataDogClientToken = clientToken;
        }
    }
    // Create a scoped logger with an optional context.
    // Will create log messages of the format:
    // [<scope>] [context_key1: context_value1, context_key_2: context_value_2...] message <args>
    static getLogger(scope, context) {
        if (LoggerProvider._loggers.has(scope)) {
            return LoggerProvider._loggers.get(scope);
        }
        const logger = new Logger(scope, context);
        LoggerProvider._loggers.set(scope, logger);
        if (LoggerProvider.dataDogClientToken != '') {
            const dd = browser_logs_1.datadogLogs.createLogger(scope, {
                context: context,
                handler: browser_logs_1.HandlerType.http
            });
            dd.setContext(context || {});
            logger.setDataDogTransport(dd);
        }
        return logger;
    }
    // Remove any created loggers and DataDog client tokens
    static removeAll() {
        LoggerProvider._loggers.clear();
        LoggerProvider.dataDogClientToken = '';
    }
    // Set the global log level
    static setLevel(level) {
        this._loggers.forEach((logger) => {
            logger.level = level;
        });
    }
}
exports.LoggerProvider = LoggerProvider;
LoggerProvider._loggers = new Map();
LoggerProvider.dataDogClientToken = '';
//# sourceMappingURL=LoggerProvider.js.map